import React, { useState, useMemo } from "react";
import { Box, Typography, Paper, Grid, Button, Menu, MenuItem } from "@mui/material";
import { LocationOn, CalendarToday, KeyboardArrowDown } from "@mui/icons-material";
import classes from "./EventsPage.module.scss";
import eventsData from "./eventsData.json";

// Import event images
import aiMockTrialImg from "../../assets/images/ai-mock-trial-june-5.avif";
import entrepreneurshipImg from "../../assets/images/entrepreneurship-first-50-customers-june-12.avif";
import sistersSoireeImg from "../../assets/images/sisters-soiree-workshop-june-14.avif";
import maxPitchDinnerImg from "../../assets/images/MAXPitch-GP-LP-Dinner-Final.png";
import executiveLeadershipImg from "../../assets/images/executive-leadership-june-18.jpeg";

interface Event {
    id: string;
    title: string;
    date: string;
    location: string;
    price: string;
    eventUrl: string;
    imageUrl: string | null;
    isPrivate?: boolean;
}

// Create a mapping from image filename to imported image object
const eventImages: { [key: string]: string } = {
    "ai-mock-trial-june-5.avif": aiMockTrialImg,
    "entrepreneurship-first-50-customers-june-12.avif": entrepreneurshipImg,
    "sisters-soiree-workshop-june-14.avif": sistersSoireeImg,
    "MAXPitch-GP-LP-Dinner-Final.png": maxPitchDinnerImg,
    "executive-leadership-june-18.jpeg": executiveLeadershipImg,
};

// Filter options
const DATE_FILTERS = ["All Dates", "Today", "This Week", "This Month", "Next Month"];

const EventsPage: React.FC = () => {
    // Filter states
    const [dateFilter, setDateFilter] = useState("All Dates");
    const [locationFilter, setLocationFilter] = useState("All Locations");

    // Menu anchor states
    const [dateAnchorEl, setDateAnchorEl] = useState<null | HTMLElement>(null);
    const [locationAnchorEl, setLocationAnchorEl] = useState<null | HTMLElement>(null);

    // Get unique locations from events data
    const locations = useMemo(() => {
        const uniqueLocations = Array.from(
            new Set(eventsData.events.map((event) => event.location)),
        );
        return ["All Locations", ...uniqueLocations];
    }, []);

    // Filter functions
    const filterByDate = (event: Event) => {
        if (dateFilter === "All Dates") return true;

        const eventDate = new Date(event.date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const weekFromNow = new Date(today);
        weekFromNow.setDate(weekFromNow.getDate() + 7);

        switch (dateFilter) {
            case "Today":
                return eventDate.toDateString() === today.toDateString();
            case "This Week":
                return eventDate >= today && eventDate <= weekFromNow;
            case "This Month": {
                const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                return eventDate >= firstDayOfMonth && eventDate <= lastDayOfMonth;
            }
            case "Next Month": {
                const firstDayOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
                const lastDayOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 2, 0);
                return eventDate >= firstDayOfNextMonth && eventDate <= lastDayOfNextMonth;
            }
            default:
                return true;
        }
    };

    const filterByLocation = (event: Event) => {
        return locationFilter === "All Locations" || event.location === locationFilter;
    };

    // Apply all filters and sort events
    const filteredEvents = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const isPast = (event: Event) => new Date(event.date) < today;

        return eventsData.events
            .filter((event) => filterByDate(event) && filterByLocation(event))
            .sort((a, b) => {
                const aIsPast = isPast(a);
                const bIsPast = isPast(b);

                if (aIsPast && !bIsPast) return 1;
                if (!aIsPast && bIsPast) return -1;

                // If both are upcoming, sort ascending
                if (!aIsPast) {
                    return new Date(a.date).getTime() - new Date(b.date).getTime();
                }

                // If both are past, sort descending
                return new Date(b.date).getTime() - new Date(a.date).getTime();
            });
    }, [dateFilter, locationFilter]);

    // Menu handlers
    const handleMenuOpen = (
        event: React.MouseEvent<HTMLElement>,
        setAnchor: (el: HTMLElement | null) => void,
    ) => {
        setAnchor(event.currentTarget);
    };

    const handleMenuClose = (setAnchor: (el: HTMLElement | null) => void) => {
        setAnchor(null);
    };

    return (
        <>
            {/* Banner Section */}
            <Paper className={classes.banner} elevation={0}>
                <Box className={classes.bannerContent}>
                    <Typography variant="h3" className={classes.bannerTitle}>
                        Events
                    </Typography>
                    <Typography variant="subtitle1" className={classes.bannerSubtitle}>
                        Discover and join events in your community
                    </Typography>
                </Box>
            </Paper>

            {/* Main Content Grid */}
            <Box className={classes.pageContainer}>
                <Grid container spacing={3} className={classes.mainContent}>
                    {/* Events List Column */}
                    <Grid item xs={12} md={8}>
                        <Box className={classes.eventsContainer}>
                            {filteredEvents.length > 0 ? (
                                filteredEvents.map((event: Event) => {
                                    const eventDate = new Date(event.date);
                                    const today = new Date();
                                    today.setHours(0, 0, 0, 0); // Set to midnight to compare dates only
                                    const isPast = eventDate < today;
                                    const imageUrl = event.imageUrl
                                        ? eventImages[event.imageUrl]
                                        : null;

                                    return (
                                        <Paper
                                            key={event.id}
                                            className={`${classes.eventCard} ${isPast ? classes.pastEvent : ""}`}
                                            elevation={1}
                                        >
                                            {/* Image Section */}
                                            <Box className={classes.eventImageContainer}>
                                                {imageUrl ? (
                                                    <img
                                                        src={imageUrl}
                                                        alt={event.title}
                                                        className={classes.eventImage}
                                                    />
                                                ) : (
                                                    <Box className={classes.placeholderImage}>
                                                        <CalendarToday sx={{ fontSize: 64 }} />
                                                    </Box>
                                                )}
                                            </Box>

                                            {/* Content Section */}
                                            <Box className={classes.eventContent}>
                                                <Typography
                                                    variant="h5"
                                                    className={classes.eventTitle}
                                                >
                                                    {event.title}
                                                </Typography>

                                                <Box className={classes.eventDetails}>
                                                    <Box className={classes.detailItem}>
                                                        <CalendarToday fontSize="small" />
                                                        <Typography variant="body2">
                                                            {event.date}
                                                        </Typography>
                                                    </Box>
                                                    <Box className={classes.detailItem}>
                                                        <LocationOn fontSize="small" />
                                                        <Typography variant="body2">
                                                            {event.location}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                {!event.isPrivate && (
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        href={event.eventUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className={classes.viewButton}
                                                    >
                                                        View Event
                                                    </Button>
                                                )}
                                            </Box>
                                        </Paper>
                                    );
                                })
                            ) : (
                                <Box className={classes.noResults}>
                                    <Typography variant="h6">No events found</Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Try adjusting your filters to see more events
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Grid>

                    {/* Filters Column */}
                    <Grid item xs={12} md={4}>
                        <Paper className={classes.filtersContainer} elevation={1}>
                            <Typography variant="h6" className={classes.filtersTitle}>
                                Filter Events
                            </Typography>

                            {/* Date Filter */}
                            <Box className={classes.filterSection}>
                                <Typography variant="subtitle2">Date</Typography>
                                <Button
                                    variant="outlined"
                                    className={classes.filterButton}
                                    fullWidth
                                    onClick={(e) => handleMenuOpen(e, setDateAnchorEl)}
                                    endIcon={<KeyboardArrowDown />}
                                >
                                    {dateFilter}
                                </Button>
                                <Menu
                                    anchorEl={dateAnchorEl}
                                    open={Boolean(dateAnchorEl)}
                                    onClose={() => handleMenuClose(setDateAnchorEl)}
                                >
                                    {DATE_FILTERS.map((filter) => (
                                        <MenuItem
                                            key={filter}
                                            onClick={() => {
                                                setDateFilter(filter);
                                                handleMenuClose(setDateAnchorEl);
                                            }}
                                            selected={dateFilter === filter}
                                        >
                                            {filter}
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </Box>

                            {/* Location Filter */}
                            <Box className={classes.filterSection}>
                                <Typography variant="subtitle2">Location</Typography>
                                <Button
                                    variant="outlined"
                                    className={classes.filterButton}
                                    fullWidth
                                    onClick={(e) => handleMenuOpen(e, setLocationAnchorEl)}
                                    endIcon={<KeyboardArrowDown />}
                                >
                                    {locationFilter}
                                </Button>
                                <Menu
                                    anchorEl={locationAnchorEl}
                                    open={Boolean(locationAnchorEl)}
                                    onClose={() => handleMenuClose(setLocationAnchorEl)}
                                >
                                    {locations.map((location) => (
                                        <MenuItem
                                            key={location}
                                            onClick={() => {
                                                setLocationFilter(location);
                                                handleMenuClose(setLocationAnchorEl);
                                            }}
                                            selected={locationFilter === location}
                                        >
                                            {location}
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};

export default EventsPage;
